type ExtractContentTextOptions = {
  partSeparator?: string;
  textOnlyParts?: boolean;
  trimText?: boolean;
};

export function safeJsonParse(content: string): unknown | undefined {
  const trimmed = content.trim();
  if (!trimmed) return undefined;
  if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && /[}\]]$/.test(trimmed)) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export function extractDisplayText(parsed: unknown): string {
  return extractContentText(parsed);
}

function extractTextFromParts(parts: unknown[], options: ExtractContentTextOptions): string {
  const texts = parts
    .map((part) => {
      if (typeof part === 'string') return options.trimText ? part.trim() : part;
      if (typeof part === 'object' && part) {
        const p = part as Record<string, unknown>;
        // Normalize provider-specific part shapes by reading text from either
        // `text` or `content`, and optionally ignore non-text parts entirely.
        const partType = typeof p.type === 'string' ? p.type.trim() : '';
        if (options.textOnlyParts && partType && partType !== 'text') return '';
        const raw =
          typeof p.text === 'string' ? p.text : typeof p.content === 'string' ? p.content : '';
        if (!raw) return '';
        return options.trimText ? raw.trim() : raw;
      }
      return '';
    })
    .filter(Boolean);
  return texts.length > 0 ? texts.join(options.partSeparator ?? '\n\n') : '';
}

export function extractContentText(
  content: unknown,
  options: ExtractContentTextOptions = {},
): string {
  // Handle plain string (e.g. from streaming or legacy thread entries)
  if (typeof content === 'string') {
    return options.trimText ? content.trim() : content;
  }

  if (!content || typeof content !== 'object') return '';

  // Handle raw array of content parts (streaming shape)
  if (Array.isArray(content)) {
    return extractTextFromParts(content, options);
  }

  const record = content as Record<string, unknown>;
  if (Array.isArray(record.parts)) {
    return extractTextFromParts(record.parts, options);
  }

  if (typeof record.content === 'string') {
    return options.trimText ? record.content.trim() : record.content;
  }

  if (typeof record.text === 'string') {
    return options.trimText ? record.text.trim() : record.text;
  }

  return '';
}

export function prettifyJson(parsed: unknown): string {
  try {
    return JSON.stringify(parsed, null, 2);
  } catch {
    return typeof parsed === 'string' ? parsed : '';
  }
}
